import { type INode, type Point } from '@ardoq/yfiles';
import { type ArdoqId, ViewIds } from '@ardoq/api-types';
import { type CollapsibleGraphGroup, TAB_HEIGHT } from '@ardoq/graph';
import {
  MODERNIZED_BLOCK_DIAGRAM_EXPANDER_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING,
} from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import { EXPANDER_RADIUS } from 'tabview/blockDiagram/view/consts';
import {
  CONTEXT_HIGHLIGHT_PADDING,
  HALO_PADDING,
} from 'yfilesExtensions/styles/consts';

type HitTestExpanderArgs = {
  node: INode;
  location: Point;
  viewId: ViewIds;
  contextComponentId: ArdoqId;
};

const hitTestBlockDiagramExpander = (
  { tag, layout: { x: groupX, y: groupY, height: groupHeight } }: INode,
  { x, y }: Point,
  contextComponentId: ArdoqId
): boolean => {
  const graphGroup = tag as CollapsibleGraphGroup;
  const { collapsed, modelId } = graphGroup;
  if (collapsed && graphGroup.isComponent()) {
    const isContext = contextComponentId === modelId;
    const offsetX = isContext ? CONTEXT_HIGHLIGHT_PADDING - HALO_PADDING : 0;
    const expanderCenterX = groupX + offsetX;
    const expanderLeft = expanderCenterX - EXPANDER_RADIUS / 2;
    const expanderRight = expanderCenterX + EXPANDER_RADIUS / 2;
    const expanderCenterY = groupY + groupHeight / 2;
    const expanderTop = expanderCenterY - EXPANDER_RADIUS / 2;
    const expanderBottom = expanderCenterY + EXPANDER_RADIUS / 2;
    return (
      x >= expanderLeft &&
      x <= expanderRight &&
      y >= expanderTop &&
      y <= expanderBottom
    );
  }
  const expanderCenterX = groupX + TAB_HEIGHT / 2;
  const expanderLeft = expanderCenterX - EXPANDER_RADIUS / 2;
  const expanderRight = expanderCenterX + EXPANDER_RADIUS / 2;
  const expanderCenterY = groupY + TAB_HEIGHT / 2;
  const expanderTop = expanderCenterY - EXPANDER_RADIUS / 2;
  const expanderBottom = expanderCenterY + EXPANDER_RADIUS / 2;
  return (
    x >= expanderLeft &&
    x <= expanderRight &&
    y >= expanderTop &&
    y <= expanderBottom
  );
};

const hitTestModernizedExpander = (
  { layout: { x: groupX, y: groupY } }: INode,
  { x, y }: Point
): boolean => {
  const expanderLeft = groupX + MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING;
  const expanderRight = expanderLeft + MODERNIZED_BLOCK_DIAGRAM_EXPANDER_SIZE;
  const expanderTop = groupY + MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING;
  const expanderBottom = expanderTop + MODERNIZED_BLOCK_DIAGRAM_EXPANDER_SIZE;
  return (
    x >= expanderLeft &&
    x <= expanderRight &&
    y >= expanderTop &&
    y <= expanderBottom
  );
};

const hitTestExpander = ({
  node,
  location,
  viewId,
  contextComponentId,
}: HitTestExpanderArgs): boolean => {
  switch (viewId) {
    case ViewIds.BLOCK_DIAGRAM:
      return hitTestBlockDiagramExpander(node, location, contextComponentId);
    case ViewIds.MODERNIZED_BLOCK_DIAGRAM:
      return hitTestModernizedExpander(node, location);
  }
  return false;
};

export { hitTestExpander, hitTestModernizedExpander };
